<script src="../router/index.js"></script>
<template>
  <div class="chat-container">

    <div class="chat-content">
      <!-- 空白对话时的欢迎页 -->
      <div class="welcome-screen" v-if="messages.length === 0">
        <div class="logo-container">
          <img src="@/assets/images/app.png" alt="AI助手" class="ai-logo">
        </div>
        <div class="welcome-text">
          <h3>欢迎使用营销AI小助手</h3>
          <p>我可以回答您营销相关的问题，内容涉及线上渠道运营、活动报名、价格管理、营销报表及数据工具使用等方面。请您输入您的问题或从下方选择常见问题开始对话。</p>
        </div>
      </div>

      <!-- 消息列表，使用v-show而不是v-else以确保DOM元素始终存在 -->
      <div class="chat-messages" ref="messagesContainer" v-show="messages.length > 0"
           :class="{ 'hidden': messages.length === 0 }">
        <div v-for="(message, index) in messages" :key="index"
             :class="['message', message.role === 'user' ? 'user-message' : 'ai-message']">
          <!-- 添加 AI 头像 -->
          <div class="avatar" v-if="message.role === 'assistant'">
            <img src="@/assets/images/app.png" alt="AI" class="avatar-img">
          </div>

          <div class="message-bubble">
            <div class="message-content">
              <!-- 对于用户消息，直接显示全部内容 -->
              <template v-if="message.role === 'user'">
                {{ message.content }}
              </template>
              <!-- 对于AI消息，使用打字效果 -->
              <template v-else>
                <div class="message-content-wrapper">
                  <div v-html="message.renderedContent || renderMarkdown(message.content)"></div>
                  <div class="typing-dots"
                       v-if="index === messages.length - 1 && message.role === 'assistant' && loading">
                    <span></span><span></span><span></span>
                  </div>
                  <button
                    class="copy-button"
                    @click="copyMessage(message.content)"
                    title="复制内容"
                    v-if="message.role === 'assistant'"
                  >
                    <svg viewBox="0 0 24 24" width="16" height="16">
                      <path fill="currentColor"
                            d="M16 1H4C2.9 1 2 1.9 2 3v14h2V3h12V1zm3 4H8C6.9 5 6 5.9 6 7v14c0 1.1.9 2 2 2h11c1.1 0 2-.9 2-2V7c0-1.1-.9-2-2-2zm0 16H8V7h11v14z"/>
                    </svg>
                  </button>
                  <div class="copy-tip" v-if="showCopyTip">{{ copyTipText }}</div>
                </div>
              </template>
            </div>
          </div>
        </div>

        <div v-if="loading" class="loading-indicator">
          <div class="loading-dots">
            <span></span><span></span><span></span>
          </div>
        </div>

        <!-- 底部空间，确保最后一条消息不被常见问题和输入框遮挡 -->
        <div class="messages-bottom-space"></div>
      </div>

      <div class="input-section">
        <div class="common-questions">
          <h3>常见问题</h3>
          <div class="questions-list">
            <div v-for="(question, index) in commonQuestions"
                 :key="index"
                 class="question-item"
                 :class="{ 'disabled': loading || isTyping }"
                 @click="!loading && !isTyping && selectCommonQuestion(question)">
              {{ question }}
            </div>
          </div>
        </div>

        <div class="chat-input">
          <form @submit.prevent="!loading && !isTyping && sendMessage()">
            <input
              type="text"
              v-model="userInput"
              placeholder="请输入您的问题，按发送按钮提交..."
              :disabled="loading || isTyping"
              enterkeyhint="send"
              inputmode="text"
              autocomplete="off"
              @focus="handleInputFocus"
              ref="chatInput"
            />
            <button
              type="submit"
              class="send-button"
              :disabled="!userInput.trim() || loading || isTyping"
            >
              <svg viewBox="0 0 24 24" width="24" height="24">
                <path fill="currentColor" d="M2.01 21L23 12 2.01 3 2 10l15 2-15 2z"/>
              </svg>
            </button>
            <div class="loading-dots" v-if="loading">
              <span></span><span></span><span></span>
            </div>
          </form>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { marked } from 'marked'
import { commonApi, streamPost } from '@/api/modules/common'
import { v4 as uuidv4 } from 'uuid'
import deviceDetection from '@/mixins/deviceDetection'

export default {
  name: 'AiChat',
  mixins: [deviceDetection],
  components: {
    // 移除 TypedText 组件
  },
  data () {
    return {
      userInput: '',
      messages: [],
      loading: false,
      isSubmitting: false,
      isTyping: false, // 跟踪打字效果是否正在进行
      eventSource: null, // 添加eventSource属性，方便管理SSE连接
      sseTimeout: null, // 添加超时定时器属性
      commonQuestions: [
        // '什么是人工智能？',
        // '如何使用这·个聊天功能？',
        // '你能做什么？',
      ],
      messageUUId: '',
      marked: marked, // 将marked添加到data中
      showCopyTip: false,
      copyTipText: '',
    }
  },
  computed: {
    lastAIRenderedContent () {
      console.log('len:com:', this.messages.length)
      const lastMessage = this.messages[this.messages.length - 1]
      if (!lastMessage || lastMessage.role !== 'assistant') return ''

      // 每次重新编译整个内容（保持Markdown结构完整）
      return marked(lastMessage.content || '')
    }
  },
  methods: {
    // 添加一个方法来处理markdown渲染
    renderMarkdown (content) {
      return this.marked(content)
    },
    async sendMessage () {
      if (this.eventSource) this.eventSource = null
      if (!this.userInput.trim() || this.loading || this.isTyping) return

      const userMessage = {
        role: 'user',
        content: this.userInput,
        time: this.formatTime(new Date())
      }

      this.messages.push(userMessage)
      const userQuery = this.userInput
      this.userInput = ''
      this.loading = true
      this.isSubmitting = true

      this.dismissKeyboard()

      try {
        this.$nextTick(() => {
          this.safeScrollToBottom()
        })

        const aiMessage = {
          role: 'assistant',
          content: '',
          renderedContent: '', // 添加renderedContent字段
          time: this.formatTime(new Date())
        }
        this.messages.push(aiMessage)

        const streamUrl = `https://api.shinyotech.com/sse/admin/platform/chat/stream`
        this.eventSource = streamPost(streamUrl, {
          'question': userQuery,
          uuid: this.messageUUId
        }, (e) => {
          const data = e
          const lastIndex = this.messages.length - 1
          if (lastIndex >= 0 && this.messages[lastIndex].role === 'assistant') {
            this.messages[lastIndex].content += data.content
            if (data.content === '[END]' || data.content === '[DONE]' ||
              data.content.includes('[END]') || data.content.includes('[DONE]')) {
              this.messages[lastIndex].content = this.messages[lastIndex].content
                .replace(/\[END\]|\[DONE\]/g, '').trim()
              if (this.eventSource) this.eventSource = null
              this.loading = false
              this.isSubmitting = false
            }

            // 更新当前消息的渲染内容
            this.messages[lastIndex].renderedContent = marked(this.messages[lastIndex].content)

            this.$nextTick(() => {
              this.safeScrollToBottom()
            })
          }
        }, (error) => {
          console.error('SSE错误:', error)
          if (this.eventSource) this.eventSource = null
          this.loading = false
          this.isSubmitting = false
        }, (data) => {
          console.log('Stream completed:', data)
          this.loading = false
          this.isSubmitting = false
        })
      } catch (error) {
        console.error('AI 回复出错:', error)
        this.messages.push({
          role: 'assistant',
          content: '抱歉，我遇到了一些问题。请稍后再试。',
          renderedContent: marked('抱歉，我遇到了一些问题。请稍后再试。'),
          time: this.formatTime(new Date())
        })
        this.loading = false
        this.isSubmitting = false
      }
    }
    ,
    formatTime (date) {
      const hours = date.getHours().toString().padStart(2, '0')
      const minutes = date.getMinutes().toString().padStart(2, '0')
      return `${hours}:${minutes}`
    }
    ,
    // 安全的滚动方法
    safeScrollToBottom () {
      const container = this.$refs.messagesContainer
      if (container) {
        container.scrollTop = container.scrollHeight
      }
    }
    ,
    scrollToBottom () {
      this.safeScrollToBottom()
    }
    ,
    resetChat () {
      this.messages = []
      this.isTyping = false
    }
    ,
    handleInputFocus () {
      // 滚动到底部
      setTimeout(() => {
        this.safeScrollToBottom()

        // 确保输入框可见，使用 scrollIntoView
        const chatInput = document.querySelector('.chat-input')
        if (chatInput) {
          chatInput.scrollIntoView({
            behavior: 'smooth',
            block: 'end'
          })
        }

        // 对于iOS特别处理
        if (/iPhone|iPad|iPod/i.test(navigator.userAgent)) {
          // 稍微调整滚动位置，确保输入框完全可见
          window.scrollTo(0, document.body.scrollHeight)
        }
      }, 300)
    }
    ,
    setViewportHeight () {
      // 解决iOS Safari视口高度问题
      document.documentElement.style.setProperty(
        '--vh',
        `${window.innerHeight * 0.01}px`
      )
    },
    handleResize () {
      // 设备旋转或键盘弹出时调整视图
      setTimeout(() => {
        if (document.activeElement.tagName === 'INPUT') {
          const chatInput = document.querySelector('.chat-input')
          if (chatInput) {
            chatInput.scrollIntoView({ block: 'end' })
          }
        }
      }, 100)

      // 更新视口高度
      this.setViewportHeight()
    }
    ,
    // 让键盘收起
    dismissKeyboard () {
      // 如果输入框存在，让它失去焦点
      if (this.$refs.chatInput) {
        this.$refs.chatInput.blur()
      }

      // 为iOS设备添加额外处理
      if (/iPhone|iPad|iPod/i.test(navigator.userAgent)) {
        // 创建一个临时输入框，然后聚焦并失去焦点来强制收起键盘
        const tempInput = document.createElement('input')
        document.body.appendChild(tempInput)
        tempInput.focus()
        tempInput.blur()
        document.body.removeChild(tempInput)
      }

      // 另一种方法是添加一个透明按钮并触发点击
      document.activeElement.blur()
    },
    selectCommonQuestion (question) {
      if (this.loading || this.isTyping) return // 打字效果或加载中时不允许选择问题

      // 如果有正在进行的SSE连接，先关闭它
      if (this.eventSource) this.eventSource = null

      this.userInput = question
      this.sendMessage()
    },
    async init () {
      const data = {
        channel: ''
      }
      if (this.$route.query) {
        data.channel = this.$route.query.channel
      }
      commonApi.getQuestionList(data).then(data => {
        if (data.questionList.length > 0) {
          this.commonQuestions = data.questionList
        }
      })
    },
    async copyMessage (content) {
      try {
        // 检查是否支持clipboard API
        if (navigator.clipboard && navigator.clipboard.writeText) {
          await navigator.clipboard.writeText(content)
        } else {
          // 备用方案：使用传统的复制方法
          const textArea = document.createElement('textarea')
          textArea.value = content
          textArea.style.position = 'fixed'
          textArea.style.left = '-999999px'
          textArea.style.top = '-999999px'
          document.body.appendChild(textArea)
          textArea.focus()
          textArea.select()

          try {
            document.execCommand('copy')
          } catch (err) {
            console.error('复制失败:', err)
            throw err
          } finally {
            document.body.removeChild(textArea)
          }
        }

        this.showCopyTip = true
        this.copyTipText = '复制成功'
        setTimeout(() => {
          this.showCopyTip = false
        }, 2000)
      } catch (err) {
        this.showCopyTip = true
        this.copyTipText = '复制失败'
        setTimeout(() => {
          this.showCopyTip = false
        }, 2000)
        console.error('复制失败:', err)
      }
    }
  },
  mounted () {
    // 处理视口高度问题 (iOS Safari特殊处理)
    this.setViewportHeight()

    // 监听键盘弹出和设备旋转
    window.addEventListener('resize', this.handleResize)

    // 使页面默认滚动到底部
    this.$nextTick(() => {
      this.safeScrollToBottom()
    })
  },
  beforeDestroy () {
    window.removeEventListener('resize', this.handleResize)
    if (this.eventSource) this.eventSource = null
    if (this.messageUUId) this.messageUUId = ''
  },
  created () {
    // 生成一个唯一 uuid
    this.messageUUId = uuidv4()
    this.init()
  }
}
</script>

<style scoped>
:root {
  --vh: 1vh;
}

.chat-container {
  display: flex;
  flex-direction: column;
  height: calc(var(--vh, 1vh) * 100);
  max-width: 480px;
  margin: 0 auto;
  background-color: #f5f5f5;
  overflow: hidden;
  position: relative;
}

.chat-header {
  background-color: #4a90e2;
  color: white;
  padding: 12px;
  text-align: center;
  position: relative;
  z-index: 20;
  flex-shrink: 0;
}

.chat-header h2 {
  font-size: 18px;
  margin: 0;
}

.chat-content {
  flex: 1;
  display: flex;
  flex-direction: column;
  overflow: hidden;
  position: relative;
  height: 100%; /* 确保内容区域占满容器高度 */
}

/* 欢迎页样式 */
.welcome-screen {
  flex: 1;
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  padding: 20px;
  text-align: center;
  background-color: #f9f9f9;
  overflow: hidden; /* 防止滚动 */
  height: 100%; /* 确保占满容器高度 */
}

.logo-container {
  margin-bottom: 30px;
}

.ai-logo {
  width: 120px;
  height: 120px;
  object-fit: contain;
}

.welcome-text {
  max-width: 80%;
}

.welcome-text h3 {
  font-size: 22px;
  color: #4a90e2;
  margin-bottom: 15px;
}

.welcome-text p {
  font-size: 16px;
  color: #666;
  line-height: 1.6;
}

/* 消息列表样式 */
.chat-messages {
  flex: 1;
  overflow-y: auto;
  padding: 15px;
  padding-bottom: 0;
  display: flex;
  flex-direction: column;
  -webkit-overflow-scrolling: touch;
  min-height: 0; /* 防止内容溢出 */
}

.hidden {
  display: none;
}

.messages-bottom-space {
  height: 20px;
  flex-shrink: 0;
}

/* 消息样式修改 */
.message {
  max-width: 95%;
  position: relative;
  display: flex;
  align-items: flex-start;
}

/* 头像样式 */
.avatar {
  width: 36px;
  height: 36px;
  border-radius: 50%;
  margin-right: 8px;
  flex-shrink: 0;
  background-color: #f0f0f0;
  display: flex;
  align-items: center;
  justify-content: center;
  overflow: hidden;
}

.avatar-img {
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.user-avatar {
  margin-right: 0;
  margin-left: 8px;
  background-color: #dcf8c6;
}

.avatar-placeholder {
  font-size: 14px;
  color: #777;
}

/* 消息气泡样式 */
.message-bubble {
  padding: 10px 14px;
  border-radius: 18px;
  background-color: #ffffff;
  position: relative;
  max-width: 85%;
  word-wrap: break-word;
  overflow-wrap: break-word;
  margin-bottom: 4px;
  font-size: 15px;
  margin-top: 0;
}

.user-message {
  flex-direction: row-reverse;
  align-self: flex-end;
  margin-top: 15px;
  margin-bottom: 15px;
  max-width: 85%; /* 调整用户消息最大宽度 */
}

.user-message .message-bubble {
  background-color: #dcf8c6;
  border-bottom-right-radius: 5px;
  max-width: 100%; /* 让气泡占满消息容器宽度 */
  word-break: break-all; /* 允许在任意字符间换行 */
  margin-top: 0;
  padding: 10px 14px;
  white-space: normal; /* 使用正常的换行规则 */
}

.ai-message {
  margin-top: 0;
}

.ai-message .message-bubble {
  margin-top: 0;
}

.message-content {
  padding-bottom: 0;
  padding-left: 0;
  word-break: normal;
  font-size: 15px;
}

/* 覆盖marked转换后的列表样式 */
.message-content :deep(ul),
.message-content :deep(ol) {
  padding-left: 1.2em;
  margin: 0.4em 0;
}

.message-content :deep(ul > li),
.message-content :deep(ol > li) {
  margin: 0.05em 0 !important;
  padding: 0 !important;
  line-height: 1.3 !important;
}

.message-content :deep(ul ul > li),
.message-content :deep(ol ol > li),
.message-content :deep(ul ol > li),
.message-content :deep(ol ul > li) {
  margin: 0.05em 0 !important;
  padding: 0 !important;
  line-height: 1.3 !important;
}

.message-content :deep(ul ul),
.message-content :deep(ol ol),
.message-content :deep(ul ol),
.message-content :deep(ol ul) {
  margin: 0.05em 0 !important;
  padding-left: 1.2em !important;
}

/* 调整标题与列表之间的间距 */
.message-content :deep(h1),
.message-content :deep(h2),
.message-content :deep(h3),
.message-content :deep(h4),
.message-content :deep(h5),
.message-content :deep(h6) {
  margin: 0.3em 0 !important;
  line-height: 1.3 !important;
}

/* 确保列表与标题之间的间距合适 */
.message-content :deep(h1 + ul),
.message-content :deep(h2 + ul),
.message-content :deep(h3 + ul),
.message-content :deep(h4 + ul),
.message-content :deep(h5 + ul),
.message-content :deep(h6 + ul),
.message-content :deep(h1 + ol),
.message-content :deep(h2 + ol),
.message-content :deep(h3 + ol),
.message-content :deep(h4 + ol),
.message-content :deep(h5 + ol),
.message-content :deep(h6 + ol) {
  margin-top: 5px !important; /* 设置为5px */
}

/* 确保p标签内的文本也保持紧凑 */
.message-content :deep(p) {
  margin: 0.4em 0;
  max-width: 100%;
  overflow-x: hidden;
  line-height: 1.4;
}

.message-content :deep(code) {
  background-color: #f5f5f5;
  padding: 2px 4px;
  border-radius: 3px;
  font-family: monospace;
  display: inline-block;
  max-width: 100%;
  word-break: break-word;
  overflow-wrap: break-word;
}

.message-content :deep(pre) {
  max-width: 100%;
  overflow-x: auto;
  background-color: #f5f5f5;
  padding: 10px;
  border-radius: 4px;
  margin: 0.5em 0;
  word-break: break-word;
  overflow-wrap: break-word;
}

.message-content :deep(pre code) {
  display: block;
  padding: 0;
  background: none;
  word-break: break-word;
  overflow-wrap: break-word;
}

.message-content :deep(blockquote) {
  margin: 0.4em 0;
  padding-left: 0.8em;
  border-left: 2px solid #ddd;
  color: #666;
  max-width: 100%;
  overflow-x: hidden;
}

.message-content :deep(img) {
  max-width: 100%;
  height: auto;
}

.message-content :deep(table) {
  max-width: 100%;
  border-collapse: collapse;
  margin: 0.5em 0;
  overflow-x: auto;
  display: block;
}

.message-content :deep(table td),
.message-content :deep(table th) {
  border: 1px solid #ddd;
  padding: 8px;
}

/* 针对微信浏览器的特殊处理 */
@media screen and (-webkit-min-device-pixel-ratio: 0) {
  .message-content {
    -webkit-text-size-adjust: 100%;
  }
}

/* 调整Markdown转换后的列表样式 */
.message-content :deep(li) {
  margin: 0.15em 0;
}

.message-time {
  font-size: 11px;
  color: #999;
  text-align: right;
  margin-top: 5px;
}

/* 加载指示器样式修改 */
.loading-indicator {
  display: none;
}

.loading-dots {
  position: absolute;
  right: 16px;
  display: flex;
  gap: 4px;
}

.loading-dots span {
  width: 6px;
  height: 6px;
  background-color: #4a90e2;
  border-radius: 50%;
  display: inline-block;
  animation: bounce 1.4s infinite ease-in-out both;
}

.loading-dots span:nth-child(1) {
  animation-delay: -0.32s;
}

.loading-dots span:nth-child(2) {
  animation-delay: -0.16s;
}

@keyframes bounce {
  0%, 80%, 100% {
    transform: scale(0);
  }
  40% {
    transform: scale(1.0);
  }
}

/* 打字点样式 */
.typing-dots {
  display: inline-flex;
  gap: 4px;
  margin-left: 4px;
  vertical-align: middle;
}

.typing-dots span {
  width: 6px;
  height: 6px;
  background-color: #4a90e2;
  border-radius: 50%;
  display: inline-block;
  animation: bounce 1.4s infinite ease-in-out both;
}

.typing-dots span:nth-child(1) {
  animation-delay: -0.32s;
}

.typing-dots span:nth-child(2) {
  animation-delay: -0.16s;
}

@keyframes bounce {
  0%, 80%, 100% {
    transform: scale(0);
  }
  40% {
    transform: scale(1.0);
  }
}

/* 常见问题和输入区域 */
.input-section {
  flex-shrink: 0;
  position: relative;
  z-index: 10;
  background-color: #f5f5f5;
  border-top: 1px solid #eaeaea;
  width: 100%; /* 确保宽度100% */
}

.common-questions {
  padding: 12px;
  background-color: #f9f9f9;
}

.common-questions h3 {
  margin-top: 0;
  margin-bottom: 10px;
  font-size: 16px;
  color: #666;
}

.questions-list {
  display: flex;
  flex-wrap: wrap;
  gap: 10px;
}

.question-item {
  background-color: #fff;
  padding: 8px 12px;
  border-radius: 15px;
  font-size: 15px;
  border: 1px solid #e0e0e0;
  cursor: pointer;
  transition: background-color 0.2s;
}

.question-item:hover:not(.disabled) {
  background-color: #f0f0f0;
}

.question-item.disabled {
  opacity: 0.6;
  cursor: not-allowed;
  pointer-events: none;
}

.chat-input {
  display: flex;
  padding: 12px;
  background-color: #fff;
  position: relative;
}

.chat-input form {
  display: flex;
  width: 100%;
  align-items: center;
  position: relative;
}

.chat-input input {
  flex: 1;
  padding: 12px 16px;
  padding-right: 48px;
  border: 1px solid #ddd;
  border-radius: 20px;
  outline: none;
  font-size: 15px;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
  transition: border-color 0.3s, box-shadow 0.3s;
}

.send-button {
  position: absolute;
  right: 16px;
  background: none;
  border: none;
  padding: 8px;
  cursor: pointer;
  color: #4a90e2;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: color 0.3s;
}

.send-button:disabled {
  color: #ccc;
  cursor: not-allowed;
}

.send-button:not(:disabled):hover {
  color: #357abd;
}

.loading-dots {
  position: absolute;
  right: 48px; /* 调整位置，避免与发送按钮重叠 */
  display: flex;
  gap: 4px;
}

.loading-dots span {
  width: 6px;
  height: 6px;
  background-color: #4a90e2;
  border-radius: 50%;
  display: inline-block;
  animation: bounce 1.4s infinite ease-in-out both;
}

.loading-dots span:nth-child(1) {
  animation-delay: -0.32s;
}

.loading-dots span:nth-child(2) {
  animation-delay: -0.16s;
}

@keyframes bounce {
  0%, 80%, 100% {
    transform: scale(0);
  }
  40% {
    transform: scale(1.0);
  }
}

/* 针对移动设备的优化 */
@media (max-height: 600px) {
  .chat-input {
    padding: 8px;
  }

  .chat-input input {
    padding: 10px 14px;
  }

  .common-questions {
    padding: 8px;
    max-height: 120px;
    overflow-y: auto;
  }

  .question-item {
    padding: 6px 10px;
    font-size: 13px;
  }
}

/* 增加滚动流畅度 */
* {
  -webkit-tap-highlight-color: transparent;
}

/* 禁用页面拖动刷新 */
html, body {
  overscroll-behavior-y: contain;
}

.message-content-wrapper {
  position: relative;
  width: 100%;
  padding-bottom: 32px; /* 增加底部内边距，为复制按钮留出空间 */
}

.copy-button {
  position: absolute;
  right: 0;
  bottom: 4px; /* 调整底部距离 */
  background: none;
  border: none;
  padding: 4px;
  cursor: pointer;
  color: #666;
  opacity: 1;
  transition: color 0.3s;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 24px;
  height: 24px;
  border-radius: 4px;
  z-index: 1; /* 确保按钮在内容之上 */
}

.copy-button:hover {
  color: #4a90e2;
  background-color: rgba(74, 144, 226, 0.1);
}

.copy-tip {
  position: fixed;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  background-color: rgba(0, 0, 0, 0.7);
  color: white;
  padding: 8px 16px;
  border-radius: 4px;
  font-size: 14px;
  z-index: 1000;
  animation: fadeInOut 2s ease-in-out;
}

@keyframes fadeInOut {
  0% {
    opacity: 0;
  }
  20% {
    opacity: 1;
  }
  80% {
    opacity: 1;
  }
  100% {
    opacity: 0;
  }
}
</style>
